import { useCallback, useEffect, useRef, useState } from "react";
import { client } from "../Clients";
import type { MetricSnapshot, BCCADMINAPIControllersMetricPoint } from "../API/Models";

export interface StatHistoryPoint {
	date: string;
	snapshot: MetricSnapshot;
}

/** Drop history points with no snapshot : the charts can't render a dated hole. */
const normalizePoint = (point?: BCCADMINAPIControllersMetricPoint | null): StatHistoryPoint | null => {
	if (!point?.snapshot) {
		return null;
	}
	return {
		date: point.date ?? "",
		snapshot: point.snapshot
	};
};

/**
 * Content/system metrics for the Metrics page: today's snapshot plus a 7-day history via
 * GET /api/metrics. The current point is appended to `history` so trend charts include today
 * without special-casing it. `loading` covers the initial fetch (skeletons); `isRefreshing`
 * covers manual refresh(), which keeps the stale data on screen while the new load runs.
 * isMountedRef guards every setState because the fetch can outlive the page.
 */
export const useStatsData = () => {
	const [stats, setStats] = useState<MetricSnapshot | null>(null);
	const [history, setHistory] = useState<StatHistoryPoint[]>([]);
	const [loading, setLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const isMountedRef = useRef(true);

	const fetchStats = useCallback(
		async (manual = false) => {
			if (manual) {
				setIsRefreshing(true);
			} else {
				setLoading(true);
			}
			setError(null);
			try {
				const response = await client.metrics.apiMetricsGet(7);
				if (!isMountedRef.current) return;

				const historyPoints =
					response.data?.history
						?.map((point) => normalizePoint(point))
						.filter((point): point is StatHistoryPoint => Boolean(point)) ?? [];

				const currentPoint = normalizePoint(response.data?.current);

				setStats(currentPoint?.snapshot ?? null);
				setHistory([
					...historyPoints,
					...(currentPoint ? [currentPoint] : [])
				]);
			} catch (caughtError) {
				if (!isMountedRef.current) return;
				console.error(caughtError);
				setError("Unable to load stats. Please try again.");
			} finally {
				if (!isMountedRef.current) return;
				if (manual) {
					setIsRefreshing(false);
				} else {
					setLoading(false);
				}
			}
		},
		[]
	);

	useEffect(() => {
		isMountedRef.current = true;
		fetchStats();
		return () => {
			isMountedRef.current = false;
		};
	}, [fetchStats]);

	return {
		stats,
		history,
		loading,
		error,
		isRefreshing,
		refresh: () => fetchStats(true)
	};
};

export default useStatsData;

